import type { Asset } from '#modules/AssetOverview/AssetOverview.types'
import type { ExpenseEstimateDto } from '#modules/Budget/types/expense.types'

import { FlowPage } from '@monthio/design-system'
import { LoadingScreen } from '@monthio/react-components'
import { budgetApiClient } from '#api/budgetApiClient'
import { overarchingTypeOf } from '#modules/AssetOverview/AssetOverview.types'
import { LoanType } from '#modules/Questionnaire/types/questionnaire.types'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Route, useHistory, useLocation } from 'react-router-dom'

import appConfig from '../../../config'
import { AssetExpensesIntro } from './Components/AssetExpensesIntro'
import { MissingExpenseEstimateAssetLinksPage } from './Components/MissingExpenseEstimateAssetLinksPage/MissingExpenseEstimateAssetLinksPage'
import { MonthlyAssetExpensesPage } from './Components/MonthlyAssetExpensesPage'
import { useAssetExpensesModule } from './useAssetExpensesModule'
import { useCompletedRoutes } from './useCompletedRoutes'

const ASSET_EXPENSES_ROUTE = appConfig.routeCodes.assetExpensesPage
const PROPERTY_LINK_ROUTE = `${ASSET_EXPENSES_ROUTE}/property-link`
const PROPERTY_BUDGET_ROUTE = `${ASSET_EXPENSES_ROUTE}/property-budget`
const VEHICLE_LINK_ROUTE = `${ASSET_EXPENSES_ROUTE}/vehicle-link`
const VEHICLE_BUDGET_ROUTE = `${ASSET_EXPENSES_ROUTE}/vehicle-budget`

interface AssetExpenseEstimates {
	housing: ExpenseEstimateDto[]
	car: ExpenseEstimateDto[]
}

interface AssetExpenseData {
	estimates: AssetExpenseEstimates
	hasRealEstateAssets: boolean
	hasVehicleAssets: boolean
}

const fetchAssetExpenseEstimates = async (
	assets: Asset[],
): Promise<AssetExpenseData> => {
	const hasRealEstateAssets = assets.some(
		(asset) => overarchingTypeOf(asset.assetType) === 'realEstate',
	)
	const hasVehicleAssets = assets.some(
		(asset) => overarchingTypeOf(asset.assetType) === 'vehicle',
	)

	const estimatePromises = [
		hasRealEstateAssets
			? budgetApiClient.getAssetExpenseEstimates('housing')
			: Promise.resolve({ estimates: [] }),
		hasVehicleAssets
			? budgetApiClient.getAssetExpenseEstimates('car')
			: Promise.resolve({ estimates: [] }),
	]

	const [housingEstimatesResponse, carEstimatesResponse] =
		await Promise.all(estimatePromises)
	return {
		estimates: {
			housing: housingEstimatesResponse?.estimates ?? [],
			car: carEstimatesResponse?.estimates ?? [],
		},
		hasRealEstateAssets,
		hasVehicleAssets,
	}
}

const useFetchAssetsAndEstimates = ({
	caseId,
	applicantSessionId,
}: {
	caseId: string
	applicantSessionId: string
}) => {
	const history = useHistory()
	const [assets, setAssets] = useState<Asset[]>([])
	const [estimates, setEstimates] = useState<AssetExpenseEstimates>({
		housing: [],
		car: [],
	})
	const [isFetchingData, setIsFetchingData] = useState(false)
	const [hasFetchingDataFailed, setHasFetchingDataFailed] = useState(false)
	const [routes, setRoutes] = useState<string[]>([])

	const { config, finish } = useAssetExpensesModule()

	const { pathname: currentRoute } = useLocation()
	const { completedRoutes, addCompletedRoute, clearCompletedRoutes } =
		useCompletedRoutes(
			`completedAssetExpenseRoutes::${caseId}:${applicantSessionId}`,
		)

	useEffect(() => {
		const fetchData = async () => {
			if (isFetchingData) {
				return
			}
			setIsFetchingData(true)

			const assetsData = await budgetApiClient.fetchAssets()

			const activeAssets = assetsData.filter(
				(asset) => !asset.deletedByApplicant,
			)

			const { shouldShowIndicationOfResult } = config
			const loanType = config.clientInputsType

			const showOnlyVehicleRoutes =
				shouldShowIndicationOfResult &&
				(loanType === LoanType.CarLoan || loanType === LoanType.CarLeasing)
			const showOnlyPropertyRoutes =
				shouldShowIndicationOfResult && loanType === LoanType.HomeLoan

			const {
				estimates: _estimates,
				hasRealEstateAssets,
				hasVehicleAssets,
			} = await fetchAssetExpenseEstimates(activeAssets)

			const updatedRoutes = []
			const shouldHideBudgets = config.shouldHideAssetBudgets

			if (!showOnlyVehicleRoutes && hasRealEstateAssets) {
				if (_estimates.housing.length > 0) {
					updatedRoutes.push(PROPERTY_LINK_ROUTE)
				}

				if (!shouldHideBudgets) {
					updatedRoutes.push(PROPERTY_BUDGET_ROUTE)
				}
			}

			if (!showOnlyPropertyRoutes && hasVehicleAssets) {
				if (_estimates.car.length > 0) {
					updatedRoutes.push(VEHICLE_LINK_ROUTE)
				}

				if (!shouldHideBudgets) {
					updatedRoutes.push(VEHICLE_BUDGET_ROUTE)
				}
			}

			if (updatedRoutes.length == 0) {
				// For some unknown reason, this can fire multiple times causing multiple finish calls
				finishModule()
				return
			}

			if (
				hasVehicleAssets &&
				!updatedRoutes.find((r) => r === VEHICLE_LINK_ROUTE)
			) {
				await budgetApiClient.updateAssetExpenseEstimates([], 'car')
			}

			if (
				hasRealEstateAssets &&
				!updatedRoutes.find((r) => r === PROPERTY_LINK_ROUTE)
			) {
				await budgetApiClient.updateAssetExpenseEstimates([], 'housing')
			}

			setAssets(activeAssets)
			setRoutes(updatedRoutes)
			setEstimates(_estimates)
		}

		fetchData()
			.catch(() => setHasFetchingDataFailed(true))
			.finally(() => {
				setIsFetchingData(false)
			})
	}, [])

	useEffect(() => {
		const completedSubroutes = completedRoutes.filter(
			(r) => r !== ASSET_EXPENSES_ROUTE,
		)
		if (
			routes.length > 0 &&
			currentRoute == ASSET_EXPENSES_ROUTE &&
			completedSubroutes.length > 0
		) {
			if (completedSubroutes.length === routes.length) {
				goToRoute(routes[completedSubroutes.length - 1]!)
				return
			}
			goToRoute(routes[completedSubroutes.length]!)
		}
	}, [routes, currentRoute, completedRoutes])

	const goToRoute = useCallback(
		(path: string) => {
			history.replace(path, history.location.state)
		},
		[history],
	)

	const goToNextRoute = useCallback(async () => {
		addCompletedRoute(currentRoute)
		const referenceRouteIndex = routes.indexOf(currentRoute)
		if (referenceRouteIndex >= routes.length - 1) {
			finishModule()
			return
		}
		return goToRoute(routes[referenceRouteIndex + 1]!)
	}, [routes, currentRoute])

	const finishModule = useCallback(() => {
		clearCompletedRoutes()
		finish()
	}, [clearCompletedRoutes, history, finish])

	return {
		assets,
		estimates,
		isFetchingData,
		hasFetchingDataFailed,
		goToNextRoute,
	}
}

export const AssetExpenses: React.FC = () => {
	const { t } = useTranslation()

	const { config } = useAssetExpensesModule()

	const {
		assets,
		estimates,
		isFetchingData,
		hasFetchingDataFailed,
		goToNextRoute,
	} = useFetchAssetsAndEstimates({
		caseId: config.caseId,
		applicantSessionId: config.applicantSessionId,
	})

	if (hasFetchingDataFailed) {
		return (
			<FlowPage>
				<FlowPage.Content $isCentered>
					<FlowPage.Header title={t('assetExpensesPage-failure-text')} />
				</FlowPage.Content>
			</FlowPage>
		)
	}

	if (isFetchingData) {
		return (
			<FlowPage>
				<FlowPage.Content $isCentered>
					<LoadingScreen />
				</FlowPage.Content>
			</FlowPage>
		)
	}

	return (
		<>
			<Route exact path={ASSET_EXPENSES_ROUTE}>
				<AssetExpensesIntro onCompleted={goToNextRoute} />
			</Route>
			<Route exact path={PROPERTY_LINK_ROUTE}>
				<MissingExpenseEstimateAssetLinksPage
					assets={assets}
					assetCategory="housing"
					estimates={estimates.housing}
					onCompleted={goToNextRoute}
				/>
			</Route>
			<Route exact path={PROPERTY_BUDGET_ROUTE}>
				<MonthlyAssetExpensesPage
					assets={assets}
					onCompleted={goToNextRoute}
					assetCategory="housing"
				/>
			</Route>
			<Route exact path={VEHICLE_LINK_ROUTE}>
				<MissingExpenseEstimateAssetLinksPage
					assets={assets}
					assetCategory="car"
					estimates={estimates.car}
					onCompleted={goToNextRoute}
				/>
			</Route>
			<Route exact path={VEHICLE_BUDGET_ROUTE}>
				<MonthlyAssetExpensesPage
					assets={assets}
					onCompleted={goToNextRoute}
					assetCategory="car"
				/>
			</Route>
		</>
	)
}
